import { useRef, useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { Card, CardContent } from '@/components/ui/card';
import { testimonials, projects } from '@/data/portfolio';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StarIcon } from './icons/StarIcon';

export const TestimonialsSection = () => {
  const [filteredTestimonials, setFilteredTestimonials] = useState(testimonials);
  
  const handleFilter = (projectId: string) => {
    if (projectId === "all") {
      setFilteredTestimonials(testimonials);
      return;
    }
    setFilteredTestimonials(testimonials.filter(t => t.projectId?.toString() === projectId));
  };

  if (!testimonials.length) return null;
  
  const swiperRef = useRef<any>(null);
  
  useEffect(() => {
    if (!swiperRef.current) return;
    
    const swiper = swiperRef.current.swiper;
    
    swiper.on('reachEnd', () => {
      setTimeout(() => {
        swiper.slideTo(0, 0);
        if (swiper.autoplay?.running === false) {
          swiper.autoplay?.start();
        }
      }, 1500);
    });
    
    return () => {
      swiper.off('reachEnd');
    };
  }, []);

  return (
    <section className="mb-12">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Client Testimonials</h2>
        <Select onValueChange={handleFilter}>
          <SelectTrigger className="w-[200px] bg-gray-900/50 border-gray-700">
            <SelectValue placeholder="Filter by project" />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-gray-700">
            <SelectItem value="all">All Projects</SelectItem>
            {projects.map(project => (
              <SelectItem key={project.id} value={project.id.toString()}>
                {project.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <Swiper
        ref={swiperRef}
        modules={[Autoplay, Pagination]}
        spaceBetween={16}
        slidesPerView={1}
        autoplay={{
          delay: 1500,
          disableOnInteraction: false
        }}
        pagination={{
          clickable: true,
          dynamicBullets: true
        }}
        loop={false}
        className="!pb-8"
      >
        {filteredTestimonials.map((testimonial, index) => (
          <SwiperSlide key={index}>
            <Card className="border-gray-700 bg-gradient-to-br from-gray-900/50 to-gray-800/50 relative overflow-hidden group hover:shadow-xl transition-all duration-300">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(124,58,237,0.1)_0%,_transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <CardContent className="p-6 relative z-10">
                <div className="flex items-start gap-4">
                  <div className="relative w-12 h-12 flex-shrink-0 mt-1">
                    <div className="absolute inset-0 bg-gradient-to-tr from-purple-600 to-blue-500 rounded-full flex items-center justify-center text-white font-bold shadow-md">
                      {testimonial.author.split(' ').map(n => n[0]).join('')}
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-white">{testimonial.author}</p>
                        <p className="text-sm text-gray-400">{testimonial.position}</p>
                      </div>
                      {testimonial.projectId && (
                        <span className="text-xs bg-gray-800 px-2 py-1 rounded-full text-primary">
                          Project #{testimonial.projectId}
                        </span>
                      )}
                    </div>
                    
                    <p className="text-gray-300 italic mb-4 relative pl-4">
                      <span className="absolute left-0 top-0 text-3xl text-gray-700 select-none">"</span>
                      {testimonial.text}
                      <span className="absolute right-0 bottom-0 text-3xl text-gray-700 select-none">"</span>
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, i) => (
                          <StarIcon 
                            key={i} 
                            className={`w-4 h-4 ${i < testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`}
                          />
                        ))}
                      </div>
                      <div className="text-xs text-gray-500">
                        {testimonial.date ? new Date(testimonial.date).toLocaleDateString() : null}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
};
